import { Link } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { media } from '../../config/settings/media'

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const amountReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const motionSafe = (animation) => css`
  @media (prefers-reduced-motion: no-preference) {
    ${animation}
  }
`

export const Section = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  ${media.md`
    gap: 1rem;
  `}
`

export const GeneratingWrap = styled.div`
  position: relative;
  min-height: 12rem;
`

export const HeroCard = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
  max-width: none;
  padding: 0.75rem 0.9rem 0.7rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? `
        radial-gradient(ellipse 120% 90% at 0% 0%, rgba(62, 207, 142, 0.16), transparent 55%),
        radial-gradient(ellipse 80% 70% at 100% 100%, rgba(62, 207, 142, 0.08), transparent 50%),
        ${theme.colors.background}
      `
      : `
        radial-gradient(ellipse 120% 90% at 0% 0%, rgba(62, 207, 142, 0.14), transparent 55%),
        radial-gradient(ellipse 80% 70% at 100% 100%, rgba(62, 207, 142, 0.06), transparent 50%),
        ${theme.colors.background}
      `};
  box-shadow: ${({ theme }) =>
    theme.name === 'dark'
      ? '0 8px 20px rgba(0, 0, 0, 0.18)'
      : '0 8px 20px rgba(33, 38, 45, 0.05)'};

  ${motionSafe(css`
    animation: ${fadeUp} 0.45s ease-out both;
  `)}

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${({ theme }) => theme.colors.action.primary.bg};
  }

  ${media.md`
    gap: 0.45rem;
    padding: 0.95rem 1.15rem 0.85rem;
    border-radius: 14px;
  `}
`

export const MainBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  padding-left: 0.3rem;

  ${media.md`
    padding-left: 0.4rem;
  `}
`

export const HeroTitle = styled.h2`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.textPrimary};

  ${motionSafe(css`
    animation: ${fadeUp} 0.4s ease-out both;
  `)}

  ${media.md`
    font-size: 0.975rem;
  `}
`

export const HeroAmount = styled.div`
  margin: 0;
  font-size: clamp(1.5rem, 5vw, 2rem);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;

  ${motionSafe(css`
    animation: ${amountReveal} 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.08s both;
  `)}

  ${media.md`
    font-size: 2.1rem;
  `}
`

export const HeroCaption = styled.p`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.textMuted};

  ${motionSafe(css`
    animation: ${fadeUp} 0.4s ease-out 0.1s both;
  `)}
`

export const HeroSupport = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: none;

  ${motionSafe(css`
    animation: ${fadeUp} 0.45s ease-out 0.14s both;
  `)}
`

export const HeroMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2rem 0.35rem;
  margin-top: 0.2rem;

  ${motionSafe(css`
    animation: ${fadeUp} 0.45s ease-out 0.16s both;
  `)}
`

export const HeroMetaDot = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.7;
  font-size: 0.75rem;
  line-height: 1;
`

export const HeroPrevious = styled.span`
  font-size: 0.75rem;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const TrendText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: ${({ $neutral, $negative, theme }) =>
    $neutral
      ? theme.colors.textMuted
      : $negative
        ? theme.colors.action.danger.bg
        : theme.colors.action.primary.bg};

  span.las {
    font-size: 0.85rem;
    line-height: 1;
  }
`

export const HeroFootnote = styled.p`
  margin: 0;
  margin-left: 0.35rem;
  padding-top: 0.55rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.6875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textMuted};

  ${motionSafe(css`
    animation: ${fadeUp} 0.45s ease-out 0.26s both;
  `)}

  ${media.md`
    margin-left: 0.45rem;
  `}
`

export const CardsWrapper = styled.div`
  display: grid;
  gap: 0.85rem;
  grid-template-columns: 1fr;

  ${media.sm`
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  `}
`

export const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
`

export const StatValue = styled.span`
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;

  ${media.md`
    font-size: 1.5rem;
  `}
`

export const StatLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const StatIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.14)'
      : 'rgba(62, 207, 142, 0.1)'};
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.28)'
        : 'rgba(62, 207, 142, 0.18)'};

  span {
    font-size: 1.25rem;
    line-height: 1;
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }

  ${media.md`
    width: 2.75rem;
    height: 2.75rem;

    span {
      font-size: 1.35rem;
    }
  `}
`

export const ErrorMessage = styled.p`
  margin: 0;
  padding: 1rem 1.15rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.action.danger.bg}33;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(220, 38, 38, 0.12)'
      : 'rgba(220, 38, 38, 0.06)'};
  color: ${({ theme }) => theme.colors.action.danger.bg};
  font-size: 0.875rem;
  font-weight: 500;
`

export const SetupBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`

export const SetupBannerTitle = styled.strong`
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const SetupBannerText = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const SetupBannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`

export const SetupBannerLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

export const HeroEmptyTitle = styled.p`
  margin: 0.2rem 0 0.15rem;
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.textPrimary};

  ${media.md`
    font-size: 1.25rem;
  `}
`

export const HeroEmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 36rem;
`

export const HeroEmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.85rem;
`

export const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.75rem;
  margin-top: 0.45rem;

  ${motionSafe(css`
    animation: ${fadeUp} 0.45s ease-out 0.2s both;
  `)}
`

export const HeroActionButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.25rem;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.action.primary.text};
  background: ${({ theme }) => theme.colors.action.primary.bg};
  border: 1px solid ${({ theme }) => theme.colors.action.primary.bg};
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.action.primary.bgHover};
    border-color: ${({ theme }) => theme.colors.action.primary.bgHover};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const HeroPaidStatus = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) =>
    theme.name === 'dark'
      ? theme.colors.action.primary.bg
      : theme.colors.action.primary.bgActive};
`

export const HeroUndoButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-color: ${({ theme }) => theme.colors.borderStrong};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.borderStrong};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const HeroSecondaryLink = styled(Link)`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

export const HeroPaymentError = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.action.danger.bg};
`

export const AmountMatchPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};

  ${motionSafe(css`
    animation: ${fadeUp} 0.45s ease-out 0.24s both;
  `)}
`

export const AmountMatchLabel = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const AmountMatchActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

export const AmountMatchButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.25rem;
  min-width: 4.5rem;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.06)' : theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const AmountMatchThanks = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.75rem;
  margin: 0.85rem 0 0;
  padding-top: 0.85rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};

  ${motionSafe(css`
    animation: ${fadeUp} 0.35s ease-out both;
  `)}
`

export const AmountMatchReview = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.action.primary.bg};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${({ theme }) => theme.colors.action.primary.bgHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
    border-radius: 2px;
  }
`
